/**
 * NordLabs Fullstory CRO measurement layer.
 *
 * Recording is owned by the official Fullstory Shopify app. This file adds
 * semantic page/user properties, stable element attributes, and conversion
 * events using the Fullstory Browser API v2.
 */
(function () {
  'use strict';

  var VERSION = '2026-09-25.1';
  var contextNode = document.getElementById('nordlabs-cro-context');
  if (!contextNode || window.NordLabsCRO) return;

  var context;
  try {
    context = JSON.parse(contextNode.textContent || '{}');
  } catch (error) {
    console.warn('NordLabs CRO context could not be parsed:', error);
    return;
  }

  var pendingCalls = [];
  var recentAction = null;
  var recentTracks = Object.create(null);

  function cleanProperties(properties) {
    var cleaned = {};
    Object.keys(properties || {}).forEach(function (key) {
      var value = properties[key];
      if (value === undefined || value === null || value === '') return;
      if (typeof value === 'string') value = value.slice(0, 250);
      if (Array.isArray(value)) {
        value = value.filter(function (item) {
          return item !== undefined && item !== null && item !== '';
        }).slice(0, 25);
        if (!value.length) return;
      }
      cleaned[key] = value;
    });
    return cleaned;
  }

  function callFullstory(command, payload) {
    if (typeof window.FS === 'function') {
      try {
        window.FS(command, payload);
        return true;
      } catch (error) {
        // The app embed may still be starting; retry from the small local queue.
      }
    }
    if (pendingCalls.length < 100) pendingCalls.push([command, payload]);
    return false;
  }

  function flushPending() {
    if (typeof window.FS !== 'function' || !pendingCalls.length) return;
    var calls = pendingCalls.splice(0, pendingCalls.length);
    calls.forEach(function (entry) {
      try {
        window.FS(entry[0], entry[1]);
      } catch (error) {
        // Fullstory can be unavailable when capture is blocked or consent is absent.
      }
    });
  }

  function setProperties(type, properties) {
    var cleaned = cleanProperties(properties);
    if (!Object.keys(cleaned).length) return;
    callFullstory('setProperties', { type: type, properties: cleaned });
  }

  function setUserProperties(properties) {
    setProperties('user', properties);
  }

  function track(name, properties, schema) {
    if (!name) return;
    var payload = { name: name, properties: cleanProperties(properties || {}) };
    if (schema) payload.schema = schema;
    callFullstory('trackEvent', payload);
  }

  function trackOnce(key, name, properties, intervalMs) {
    var now = Date.now();
    var interval = intervalMs || 1500;
    if (recentTracks[key] && now - recentTracks[key] < interval) return;
    recentTracks[key] = now;
    track(name, properties);
  }

  function safeStorage(storage, operation, key, value) {
    try {
      if (operation === 'get') return storage.getItem(key);
      if (operation === 'set') storage.setItem(key, value);
    } catch (error) {
      return null;
    }
    return null;
  }

  function normalizeHeadset(value) {
    var normalized = String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '_');
    if (!normalized) return '';
    if ((normalized.indexOf('quest_3') !== -1 || normalized === 'q3') && (normalized.indexOf('3s') !== -1 || normalized.indexOf('q3s') !== -1)) return 'quest_3_3s';
    if (normalized.indexOf('3s') !== -1 || normalized === 'q3s') return 'quest_3s';
    if (normalized.indexOf('quest_3') !== -1 || normalized === 'q3') return 'quest_3';
    if (normalized.indexOf('quest_2') !== -1 || normalized === 'q2') return 'quest_2';
    return normalized.slice(0, 80);
  }

  function slug(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 100);
  }

  function setInitialPageProperties() {
    setProperties('page', {
      pageName: context.pageName,
      page_type: context.pageType,
      template: context.template,
      template_suffix: context.templateSuffix,
      locale: context.locale,
      currency: context.currency,
      product_handle: context.productHandle,
      product_id: context.productId ? String(context.productId) : null,
      product_name: context.productName,
      product_type: context.productType,
      variant_id: context.variantId ? String(context.variantId) : null,
      variant_name: context.variantName,
      variant_type: context.variantType,
      collection_handle: context.collectionHandle,
      instrumentation_version: VERSION
    });

    track('CRO Page Viewed', {
      page_name: context.pageName,
      page_type: context.pageType,
      path: window.location.pathname,
      locale: context.locale,
      product_handle: context.productHandle,
      variant_type: context.variantType,
      instrumentation_version: VERSION
    });
  }

  function captureEntryAndAttribution() {
    var entryKey = 'nordlabs_fs_entry_v1';
    var attributionKey = 'nordlabs_fs_attribution_v1';
    var entry = null;
    var storedEntry = safeStorage(window.sessionStorage, 'get', entryKey);

    if (storedEntry) {
      try { entry = JSON.parse(storedEntry); } catch (error) { entry = null; }
    }

    if (!entry) {
      entry = {
        entry_page_type: context.pageType,
        entry_page_name: context.pageName,
        entry_path: window.location.pathname,
        entry_locale: context.locale
      };
      safeStorage(window.sessionStorage, 'set', entryKey, JSON.stringify(entry));
    }

    var params = new URLSearchParams(window.location.search);
    var keys = [
      'ad_id', 'adset_id', 'campaign_id',
      'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
      'fbclid', 'gclid', 'ttclid', 'angle'
    ];
    var storedAttribution = null;
    var rawStoredAttribution = safeStorage(window.localStorage, 'get', attributionKey);

    if (rawStoredAttribution) {
      try { storedAttribution = JSON.parse(rawStoredAttribution); } catch (error) { storedAttribution = null; }
    }

    var maxAge = 30 * 24 * 60 * 60 * 1000;
    if (!storedAttribution || !storedAttribution.updatedAt || Date.now() - storedAttribution.updatedAt > maxAge) {
      storedAttribution = { values: {}, updatedAt: Date.now() };
    }

    var foundCurrentAttribution = false;
    keys.forEach(function (key) {
      var value = params.get(key);
      if (!value) return;
      storedAttribution.values[key] = value.slice(0, 250);
      foundCurrentAttribution = true;
    });

    if (foundCurrentAttribution) {
      storedAttribution.updatedAt = Date.now();
      safeStorage(window.localStorage, 'set', attributionKey, JSON.stringify(storedAttribution));
    }

    setUserProperties(Object.assign({}, entry, storedAttribution.values, {
      attribution_updated_at: new Date(storedAttribution.updatedAt).toISOString(),
      instrumentation_version: VERSION
    }));
  }

  function setElementName(element, name) {
    if (!element || !name || element.getAttribute('data-cro-element')) return;
    element.setAttribute('data-cro-element', name);
  }

  function queryAll(root, selector) {
    var nodes = [];
    if (!root) return nodes;
    if (root.nodeType === 1 && root.matches && root.matches(selector)) nodes.push(root);
    if (root.querySelectorAll) nodes = nodes.concat(Array.prototype.slice.call(root.querySelectorAll(selector)));
    return nodes;
  }

  function tagElements(root) {
    var definitions = [
      ['#cart-drawer button[name="checkout"], #cart-drawer input[name="checkout"], #cart-drawer a[href*="/checkout"]', 'cart-drawer-checkout'],
      ['body.template-cart button[name="checkout"], body.template-cart input[name="checkout"], body.template-cart a[href*="/checkout"]', 'cart-page-checkout'],
      ['[data-warranty-toggle]', 'warranty-toggle'],
      ['[data-upsell-add]', 'cart-upsell-item'],
      ['[data-qf-start], [data-qf-continue], [data-qf2-start], [data-qf2-continue]', 'quiz-next'],
      ['[data-qf-back], [data-qf2-back]', 'quiz-prev'],
      ['[data-sticky-cta] button[type="submit"], [data-sticky-cta] input[type="submit"], .manual-sticky-cta-btn', 'sticky-atc-mobile']
    ];

    definitions.forEach(function (definition) {
      queryAll(root, definition[0]).forEach(function (element) {
        setElementName(element, definition[1]);
      });
    });

    queryAll(root, '[data-variant-select], [data-option-btn], .manual-option-btn, [data-option-name][data-option-value], [data-variant-options] input, [data-nvc-variant-select], [data-vrq-variant]').forEach(function (element) {
      setElementName(element, context.variantType === 'bundle' ? 'variant-selector-bundle' : 'variant-selector-single');
    });

    queryAll(root, 'form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] input[type="submit"], [data-add-to-cart-form] button[type="submit"], [data-add-to-cart-form] input[type="submit"]').forEach(function (element) {
      if (!element.getAttribute('data-cro-element')) setElementName(element, 'atc-button-pdp');
    });
  }

  function elementName(element) {
    return element && element.closest ? element.closest('[data-cro-element]')?.getAttribute('data-cro-element') : '';
  }

  function rememberAction(type, source, element) {
    recentAction = {
      type: type,
      source: source,
      elementName: elementName(element),
      at: Date.now()
    };
  }

  function currentAction() {
    if (!recentAction || Date.now() - recentAction.at > 6000) return null;
    return recentAction;
  }

  function checkoutProperties(element) {
    var drawer = !!element.closest('#cart-drawer');
    return {
      source: drawer ? 'cart_drawer' : 'cart_page',
      element_name: elementName(element),
      page_name: context.pageName,
      page_type: context.pageType
    };
  }

  function selectedQuizValue(root, selector) {
    var selected = root.querySelector(selector);
    return selected ? (selected.value || selected.getAttribute('data-val') || '') : '';
  }

  function quizCompletionProperties(root) {
    if (root.hasAttribute('data-qf2-root')) {
      var symptoms = Array.prototype.map.call(root.querySelectorAll('input[name="qf2-symptoms"]:checked'), function (input) {
        return input.value;
      });
      var profileTitle = root.querySelector('[data-qf2-profile-title]');
      var score = parseInt(root.querySelector('[data-qf2-score]')?.textContent || '', 10);
      return {
        quiz_version: 'qf2',
        headset_model: normalizeHeadset(selectedQuizValue(root, 'input[name="qf2-headset"]:checked')),
        use_case: selectedQuizValue(root, 'input[name="qf2-useCase"]:checked'),
        pain_point: slug(profileTitle?.textContent || symptoms[0]),
        symptoms: symptoms,
        score: Number.isFinite(score) ? score : null,
        score_band: root.querySelector('[data-qf2-band]')?.textContent?.trim()
      };
    }

    var painPoints = Array.prototype.map.call(root.querySelectorAll('[data-qf-question="q3"] .qf-opt.is-selected'), function (option) {
      return option.getAttribute('data-val');
    });
    var headsetOption = root.querySelector('[data-qf-question="q2"] .qf-opt.is-selected');
    var legacyScore = parseInt(root.querySelector('[data-qf-score]')?.textContent || '', 10);
    return {
      quiz_version: 'qf1',
      headset_model: normalizeHeadset(headsetOption?.getAttribute('data-headset') || headsetOption?.getAttribute('data-val')),
      pain_point: painPoints[0] || '',
      symptoms: painPoints,
      score: Number.isFinite(legacyScore) ? legacyScore : null,
      score_band: root.querySelector('[data-qf-band]')?.textContent?.trim()
    };
  }

  function detectQuizCompletion(root) {
    queryAll(root, '[data-qf-root], [data-qf2-root]').forEach(function (quizRoot) {
      if (quizRoot.getAttribute('data-cro-quiz-completed') === 'true') return;
      var result = quizRoot.querySelector('[data-screen="result"].is-active, [data-qf2-screen="result"].is-active');
      if (!result) return;

      quizRoot.setAttribute('data-cro-quiz-completed', 'true');
      var properties = quizCompletionProperties(quizRoot);
      track('Quiz Completed', Object.assign({}, properties, {
        page_name: context.pageName,
        page_type: context.pageType
      }));
      setUserProperties({
        quiz_completed: true,
        headset_model: properties.headset_model,
        quiz_pain_point: properties.pain_point,
        quiz_score: properties.score,
        quiz_score_band: properties.score_band
      });
    });
  }

  function optionGroupOf(button) {
    return button.closest('[data-option-group], .manual-option-group') || button.parentElement;
  }

  function optionNameOf(button) {
    return button.getAttribute('data-option-name') ||
      optionGroupOf(button)?.getAttribute('data-option-name') || '';
  }

  function optionValueOf(button) {
    return button.getAttribute('data-option-value') || button.getAttribute('data-value') || button.textContent?.trim() || '';
  }

  function isSelectedOption(button) {
    return button.classList.contains('manual-option-selected') || button.getAttribute('aria-pressed') === 'true';
  }

  function productRootOf(element) {
    return element.closest('[data-product-page]') || document;
  }

  function selectedOptionsIn(root) {
    var selected = {};
    queryAll(root, '.manual-option-btn, [data-option-btn], [data-option-name][data-option-value]').forEach(function (button) {
      if (isSelectedOption(button)) selected[optionNameOf(button)] = optionValueOf(button);
    });
    return selected;
  }

  function trackOptionSelection(button) {
    var optionName = optionNameOf(button);
    var optionValue = optionValueOf(button);
    if (!optionValue || isSelectedOption(button)) return; // re-clicking the current choice changes nothing
    var root = productRootOf(button);
    var previousVariantId = root.querySelector('[data-variant-id], form[action*="/cart/add"] [name="id"]')?.value || '';

    // The page resolves the variant in its own click handler; read the result right after it runs.
    window.setTimeout(function () {
      var options = selectedOptionsIn(root);
      var optionNames = Object.keys(options);
      var variantId = root.querySelector('[data-variant-id], form[action*="/cart/add"] [name="id"]')?.value || '';
      var addButton = root.querySelector('[data-add-to-cart]');
      track('Variant Selected', {
        selector_type: 'button',
        option_name: optionName,
        option_value: optionValue,
        selected_options: optionNames.map(function (name) { return name + ': ' + options[name]; }).join(' | '),
        variant_id: variantId,
        variant_title: optionNames.map(function (name) { return options[name]; }).join(' / '),
        previous_variant_id: previousVariantId,
        variant_changed: !!variantId && variantId !== previousVariantId,
        variant_available: addButton ? !addButton.disabled : null,
        product_handle: context.productHandle,
        variant_type: context.variantType,
        page_name: context.pageName
      });
      if (variantId) setProperties('page', { variant_id: variantId, variant_name: optionNames.map(function (name) { return options[name]; }).join(' / ') });
      var buttonHeadset = normalizeHeadset(optionValue);
      if (buttonHeadset.indexOf('quest_') === 0) setUserProperties({ headset_model: buttonHeadset });
    }, 0);
  }

  function bindInteractionTracking() {
    document.addEventListener('click', function (event) {
      var target = event.target.closest('button, a, input, label');
      if (!target) return;

      var checkout = target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');
      if (checkout) {
        var checkoutProps = checkoutProperties(checkout);
        trackOnce('checkout:' + checkoutProps.source, 'Checkout Clicked', checkoutProps, 2000);
      }

      var warranty = target.closest('[data-warranty-toggle]');
      if (warranty) {
        rememberAction(warranty.dataset.warrantyAdded === 'true' ? 'warranty_remove' : 'warranty_add', 'warranty_toggle', warranty);
      }

      var upsell = target.closest('[data-upsell-add]');
      if (upsell) rememberAction('cart_add', 'cart_upsell', upsell);

      var atc = target.closest('form[action*="/cart/add"] [type="submit"], [data-add-to-cart-form] [type="submit"]');
      if (atc) rememberAction('cart_add', elementName(atc) === 'sticky-atc-mobile' ? 'sticky_atc' : 'pdp_main', atc);

      var quizStart = target.closest('[data-qf-start], [data-qf2-start]');
      if (quizStart) {
        trackOnce('quiz-start', 'Quiz Started', {
          quiz_version: quizStart.closest('[data-qf2-root]') ? 'qf2' : 'qf1',
          page_name: context.pageName
        }, 5000);
      }

      var quizBack = target.closest('[data-qf-back], [data-qf2-back]');
      if (quizBack) {
        track('Quiz Back Clicked', {
          quiz_version: quizBack.closest('[data-qf2-root]') ? 'qf2' : 'qf1',
          page_name: context.pageName
        });
      }

      var legacyQuizOption = target.closest('[data-qf-question] .qf-opt');
      if (legacyQuizOption) {
        var legacyQuestion = legacyQuizOption.closest('[data-qf-question]')?.getAttribute('data-qf-question');
        var legacyAnswer = legacyQuizOption.getAttribute('data-val');
        track('Quiz Step Completed', {
          quiz_version: 'qf1',
          question: legacyQuestion,
          answer: legacyAnswer,
          page_name: context.pageName
        });
        if (legacyQuestion === 'q2') setUserProperties({ headset_model: normalizeHeadset(legacyQuizOption.getAttribute('data-headset') || legacyAnswer) });
      }

      var handoff = target.closest('[data-qf2-handoff], [data-qf2-score-handoff], .qf-cta--handoff');
      if (handoff) {
        track('Quiz Recommendation Clicked', Object.assign({}, quizCompletionProperties(handoff.closest('[data-qf-root], [data-qf2-root]')), {
          destination_path: new URL(handoff.href, window.location.origin).pathname,
          page_name: context.pageName
        }));
      }

      var optionButton = target.closest('[data-option-btn], .manual-option-btn, [data-option-name][data-option-value]');
      if (optionButton && event.isTrusted) trackOptionSelection(optionButton);
    }, true);

    document.addEventListener('submit', function (event) {
      var form = event.target.closest('form');
      if (!form) return;
      var atcForm = form.matches('[data-add-to-cart-form], form[action*="/cart/add"]');
      if (atcForm) {
        var submitter = event.submitter || form.querySelector('[type="submit"]');
        rememberAction('cart_add', elementName(submitter) === 'sticky-atc-mobile' ? 'sticky_atc' : 'pdp_main', submitter);
      }

      var checkout = form.querySelector('button[name="checkout"], input[name="checkout"]');
      if (checkout) {
        var checkoutProps = checkoutProperties(checkout);
        trackOnce('checkout:' + checkoutProps.source, 'Checkout Clicked', checkoutProps, 2000);
      }
    }, true);

    document.addEventListener('change', function (event) {
      var input = event.target;
      if (!input) return;

      if (input.closest('[data-qf2-question]')) {
        var group = input.closest('[data-qf2-question]');
        var question = group.getAttribute('data-qf2-question');
        track('Quiz Step Completed', {
          quiz_version: 'qf2',
          question: question,
          answer: input.value,
          page_name: context.pageName
        });
        if (question === 'headset') setUserProperties({ headset_model: normalizeHeadset(input.value) });
      }

      if (input.matches('[data-variant-select], [data-nvc-variant-select], [data-vrq-variant]')) {
        var selectedOption = input.options && input.options[input.selectedIndex];
        var selectedText = selectedOption?.textContent?.trim();
        track('Variant Selected', {
          selector_type: 'select',
          variant_id: input.value,
          option_value: selectedText,
          product_handle: context.productHandle,
          variant_type: context.variantType
        });
        var selectHeadset = normalizeHeadset(selectedText);
        if (selectHeadset.indexOf('quest_') === 0) setUserProperties({ headset_model: selectHeadset });
      }
    }, true);
  }

  function requestDetails(input, init) {
    var url = typeof input === 'string' ? input : input?.url;
    var method = String(init?.method || input?.method || 'GET').toUpperCase();
    var body = init?.body;
    var parsedBody = null;

    if (typeof body === 'string') {
      try { parsedBody = JSON.parse(body); } catch (error) { parsedBody = null; }
    } else if (body instanceof URLSearchParams || body instanceof FormData) {
      parsedBody = {};
      body.forEach(function (value, key) { parsedBody[key] = value; });
    }

    try {
      return { url: new URL(url, window.location.origin), method: method, body: parsedBody };
    } catch (error) {
      return { url: null, method: method, body: parsedBody };
    }
  }

  function cartItems(payload) {
    if (!payload) return [];
    if (Array.isArray(payload.items)) return payload.items;
    if (payload.id || payload.variant_id || payload.product_id) return [payload];
    return [];
  }

  function isWarrantyItem(item, action, requestBody) {
    var title = String(item.product_title || item.title || '').toLowerCase();
    var handle = String(item.handle || '').toLowerCase();
    var bodyText = '';
    try { bodyText = JSON.stringify(requestBody || {}).toLowerCase(); } catch (error) { bodyText = ''; }
    return action?.type === 'warranty_add' || title.indexOf('warranty') !== -1 || handle === 'lifetime-warranty' || bodyText.indexOf('_cartbooster_default_on') !== -1;
  }

  function itemProperties(item, source) {
    // Only fall back to the page's product details when the item is that product.
    var isPageProduct = !item.product_id || !context.productId || String(item.product_id) === String(context.productId);
    var priceCents = Number(item.final_price ?? item.price ?? (isPageProduct ? context.priceCents : NaN));
    var quantity = Math.max(parseInt(item.quantity || 1, 10), 1);
    return {
      source: source || 'unknown',
      element_name: currentAction()?.elementName,
      product_name: item.product_title || item.title || context.productName,
      product_handle: item.handle || context.productHandle,
      product_id: item.product_id ? String(item.product_id) : (context.productId ? String(context.productId) : null),
      variant_id: item.variant_id ? String(item.variant_id) : (item.id ? String(item.id) : (context.variantId ? String(context.variantId) : null)),
      variant_name: item.variant_title || (isPageProduct ? context.variantName : null),
      variant_type: context.variantType,
      sku: item.sku,
      quantity: quantity,
      price: Number.isFinite(priceCents) ? priceCents / 100 : null,
      currency: context.currency,
      page_name: context.pageName,
      page_type: context.pageType
    };
  }

  function handleCartAdd(payload, requestBody) {
    var action = currentAction();
    cartItems(payload).forEach(function (item) {
      var warranty = isWarrantyItem(item, action, requestBody);
      var source = action?.source;
      if (!source && JSON.stringify(requestBody || {}).indexOf('_cartbooster_default_on') !== -1) source = 'warranty_auto';
      var properties = itemProperties(item, source);

      if (warranty) {
        track('Warranty Added', Object.assign({}, properties, { warranty_attached: true }));
        setUserProperties({ warranty_attached: true });
      } else if (source === 'cart_upsell') {
        track('Cart Upsell Added', properties, { properties: { quantity: 'int' } });
      } else {
        track('Add To Cart', properties, { properties: { quantity: 'int' } });
      }
    });
  }

  function cartAddErrorCategory(status, payload, error) {
    if (!status) return error && error.name === 'AbortError' ? 'request_aborted' : 'network_error';
    var text = '';
    try { text = String((payload && (payload.description || payload.message)) || '').toLowerCase(); } catch (ignored) { text = ''; }
    if (status === 422 || status === 400) {
      if (/sold out|out of stock|not available|unavailable/.test(text)) return 'sold_out';
      if (/already|quantity|only \d|maximum|limit|all \d/.test(text)) return 'quantity_limit';
      if (/variant|parameter|missing|invalid|cannot find/.test(text)) return 'invalid_variant';
      return 'cart_rejected';
    }
    if (status === 429) return 'rate_limited';
    if (status === 404) return 'not_found';
    if (status >= 500) return 'server_error';
    return 'http_' + status;
  }

  function handleCartAddFailure(status, payload, requestBody, error) {
    var action = currentAction();
    var body = requestBody || {};
    if (isWarrantyItem({}, action, body)) return; // warranty toggles are reported separately and must not inflate PDP failures
    var firstItem = Array.isArray(body.items) ? body.items[0] : body;
    track('Add To Cart Failed', {
      source: action?.source || 'unknown',
      element_name: action?.elementName,
      error_category: cartAddErrorCategory(status, payload, error),
      http_status: status || null,
      variant_id: firstItem && firstItem.id ? String(firstItem.id) : null,
      quantity: firstItem && firstItem.quantity ? parseInt(firstItem.quantity, 10) : null,
      product_handle: context.productHandle,
      variant_type: context.variantType,
      page_name: context.pageName,
      page_type: context.pageType
    }, { properties: { quantity: 'int', http_status: 'int' } });
  }

  function patchFetch() {
    if (typeof window.fetch !== 'function' || window.fetch.__nordlabsCroPatched) return;
    var originalFetch = window.fetch;

    function measuredFetch(input, init) {
      var details = requestDetails(input, init);
      var result = originalFetch.apply(this, arguments);

      var isCartAdd = !!details.url && details.method === 'POST' && /\/cart\/add(?:\.js)?$/.test(details.url.pathname);

      return result.then(function (response) {
        if (isCartAdd && !response.ok) {
          response.clone().json().catch(function () { return null; }).then(function (payload) {
            handleCartAddFailure(response.status, payload, details.body);
          });
          return response;
        }
        if (!details.url || details.method !== 'POST' || !response.ok) return response;
        var pathname = details.url.pathname;

        if (/\/cart\/add(?:\.js)?$/.test(pathname)) {
          response.clone().json().then(function (payload) {
            handleCartAdd(payload, details.body);
          }).catch(function () {});
        } else if (/\/cart\/change(?:\.js)?$/.test(pathname) && currentAction()?.type === 'warranty_remove') {
          track('Warranty Removed', {
            source: currentAction()?.source,
            element_name: currentAction()?.elementName,
            page_name: context.pageName,
            warranty_attached: false
          });
          setUserProperties({ warranty_attached: false });
        }

        return response;
      }, function (error) {
        if (isCartAdd) handleCartAddFailure(0, null, details.body, error);
        throw error;
      });
    }

    measuredFetch.__nordlabsCroPatched = true;
    measuredFetch.__nordlabsOriginalFetch = originalFetch;
    window.fetch = measuredFetch;
  }


  /* ------------------------------------------------------------------------
   * Engagement: videos, sections, gallery, FAQ, specs tabs, review filters.
   *
   * Visibility rule (used everywhere): an element counts as viewed when at
   * least 50% of it is on screen, or it fills at least 50% of the viewport
   * (tall sections), continuously for 1 second.
   * Every "Viewed/Started/Progress/Completed/Opened" event fires at most once
   * per element per page view, so replays and repeat taps cannot inflate counts.
   * ---------------------------------------------------------------------- */
  var VISIBLE_MS = 1000;
  var firedOnce = Object.create(null);

  function once(key) {
    if (firedOnce[key]) return false;
    firedOnce[key] = true;
    return true;
  }

  function coverage(entry) {
    var rect = entry.boundingClientRect;
    var viewport = window.innerHeight || document.documentElement.clientHeight || 1;
    var ofElement = entry.intersectionRatio;
    var ofViewport = entry.intersectionRect.height / viewport;
    return Math.max(ofElement, ofViewport) >= 0.5 && rect.width > 0 && rect.height > 0;
  }

  function observeVisibility(elements, onVisible) {
    if (!('IntersectionObserver' in window) || !elements.length) return;
    var timers = new Map();
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var element = entry.target;
        if (entry.isIntersecting && coverage(entry)) {
          if (timers.has(element)) return;
          timers.set(element, window.setTimeout(function () {
            timers.delete(element);
            observer.unobserve(element);
            onVisible(element);
          }, VISIBLE_MS));
        } else if (timers.has(element)) {
          window.clearTimeout(timers.get(element));
          timers.delete(element);
        }
      });
    }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
    elements.forEach(function (element) { observer.observe(element); });
  }

  // ---- Videos ------------------------------------------------------------
  function videoFileId(src) {
    var match = String(src || '').match(/\/v\/([0-9a-f]{8})/i);
    return match ? match[1].toLowerCase() : '';
  }

  function describeVideo(host) {
    var video = host.tagName === 'VIDEO' ? host : host.querySelector('video');
    var type = host.getAttribute('data-video-type') || (host.hasAttribute('data-video-card') ? 'review' : 'installation');
    var src = host.getAttribute('data-src') || video?.currentSrc || video?.querySelector('source')?.getAttribute('src') || '';
    var id = host.getAttribute('data-video-id') || (videoFileId(src) ? type + '-' + videoFileId(src) : '');
    return {
      host: host,
      video: video,
      props: {
        video_id: id,
        video_type: type,
        video_position: parseInt(host.getAttribute('data-video-position') || '0', 10) || null,
        video_file_id: videoFileId(src),
        product_handle: context.productHandle,
        page_name: context.pageName,
        template_suffix: context.templateSuffix
      }
    };
  }

  function uniqueSecondsWatched(video) {
    // video.played only contains ranges that actually played, so seeking past
    // content never counts as watching it.
    var total = 0;
    for (var i = 0; i < video.played.length; i += 1) total += video.played.end(i) - video.played.start(i);
    return total;
  }

  var MEDIA_ERRORS = { 1: 'aborted', 2: 'network', 3: 'decode', 4: 'source_not_supported' };

  function bindVideo(info) {
    var video = info.video;
    var props = info.props;
    if (!video || !props.video_id || video.__nordlabsCroVideo) return;
    video.__nordlabsCroVideo = true;
    var key = 'video:' + props.video_id;
    var seekCount = 0;
    var startTimer = null;

    function withStats(extra) {
      var duration = isFinite(video.duration) ? video.duration : 0;
      var watched = uniqueSecondsWatched(video);
      return Object.assign({}, props, {
        duration_seconds: duration ? Math.round(duration) : null,
        watched_seconds: Math.round(watched),
        watched_percent: duration ? Math.min(100, Math.round((watched / duration) * 100)) : null,
        seek_count: seekCount
      }, extra || {});
    }

    function fail(category) {
      window.clearTimeout(startTimer);
      if (!once(key + ':failed')) return;
      track('Video Playback Failed', withStats({ error_category: category }), { properties: { seek_count: 'int' } });
    }

    video.addEventListener('playing', function () {
      window.clearTimeout(startTimer);
      if (once(key + ':started')) track('Video Started', withStats(), { properties: { seek_count: 'int' } });
    });

    video.addEventListener('seeking', function () {
      if (!video.ended && video.currentTime > 0.5) seekCount += 1;
    });

    video.addEventListener('timeupdate', function () {
      var duration = video.duration;
      if (!isFinite(duration) || duration <= 0) return;
      var fraction = uniqueSecondsWatched(video) / duration;
      [25, 50, 75].forEach(function (milestone) {
        if (fraction >= milestone / 100 && once(key + ':p' + milestone)) {
          track('Video Progress', withStats({ milestone_percent: milestone }), { properties: { milestone_percent: 'int', seek_count: 'int' } });
        }
      });
      if (fraction >= 0.95 && once(key + ':completed')) {
        track('Video Completed', withStats(), { properties: { seek_count: 'int' } });
      }
    });

    video.addEventListener('error', function () {
      fail(MEDIA_ERRORS[video.error && video.error.code] || 'media_error');
    });
    Array.prototype.forEach.call(video.querySelectorAll('source'), function (source) {
      source.addEventListener('error', function () { fail('source_failed'); });
    });

    // A tap that never turns into playback (blocked, stalled or broken file).
    var clickHost = info.host.closest('[data-installation-video]') || info.host;
    clickHost.addEventListener('click', function (event) {
      if (!event.isTrusted || firedOnce[key + ':started']) return;
      window.clearTimeout(startTimer);
      startTimer = window.setTimeout(function () {
        if (!firedOnce[key + ':started'] && video.paused) fail('start_timeout');
      }, 10000);
    }, true);
  }

  function bindVideos(root) {
    var hosts = queryAll(root, '[data-video-card], video[data-video-id], [data-installation-video] video');
    var visibilityTargets = [];
    hosts.forEach(function (host) {
      var info = describeVideo(host);
      if (!info.props.video_id || host.__nordlabsCroVisible) return;
      host.__nordlabsCroVisible = true;
      bindVideo(info);
      visibilityTargets.push(host);
      host.__nordlabsCroVideoInfo = info;
    });
    observeVisibility(visibilityTargets, function (host) {
      var info = host.__nordlabsCroVideoInfo;
      if (once('video:' + info.props.video_id + ':viewed')) track('Video Viewed', info.props);
    });
  }

  // ---- Sections ----------------------------------------------------------
  function sectionIdOf(element) {
    var explicit = element.getAttribute('data-cro-section');
    if (explicit) return explicit;
    if (/__loox-reviews$/.test(element.id)) return 'loox-reviews';
    if (/recommendations/.test(element.id)) return 'recommendations';
    return '';
  }

  function bindSections(root) {
    var targets = queryAll(root, '[data-cro-section], .shopify-section[id$="__loox-reviews"], .shopify-section[id*="recommendations"]').filter(function (element) {
      if (element.__nordlabsCroSection) return false;
      element.__nordlabsCroSection = true;
      return !!sectionIdOf(element);
    });
    if (!targets.length) return;
    var ordered = queryAll(document, '[data-cro-section]');
    observeVisibility(targets, function (element) {
      var id = sectionIdOf(element);
      if (!once('section:' + id)) return;
      track('Section Viewed', {
        section_id: id,
        section_position: element.hasAttribute('data-cro-section') ? ordered.indexOf(element) + 1 : null,
        product_handle: context.productHandle,
        page_name: context.pageName,
        template_suffix: context.templateSuffix
      }, { properties: { section_position: 'int' } });
    });
  }

  // ---- Gallery -----------------------------------------------------------
  var galleryIntent = { method: '', at: 0 };

  function noteGalleryIntent(method) {
    galleryIntent = { method: method, at: Date.now() };
  }

  function galleryImageProps(image, method) {
    var images = queryAll(productRootOf(image), '.manual-gallery-image');
    return {
      image_id: image.getAttribute('data-image-id') || '',
      image_position: images.indexOf(image) + 1,
      image_count: images.length,
      view_method: method,
      product_handle: context.productHandle,
      page_name: context.pageName
    };
  }

  function trackGalleryImage(image, method) {
    var props = galleryImageProps(image, method);
    if (!props.image_id) props.image_id = 'position-' + props.image_position;
    if (!once('gallery:' + props.image_id)) return;
    track('Gallery Image Viewed', props, { properties: { image_position: 'int', image_count: 'int' } });
  }

  function bindGallery(root) {
    queryAll(root, '[data-gallery-main]').forEach(function (main) {
      if (main.__nordlabsCroGallery) return;
      main.__nordlabsCroGallery = true;
      var page = productRootOf(main);

      main.addEventListener('touchstart', function (event) { if (event.isTrusted) noteGalleryIntent('swipe'); }, { passive: true, capture: true });
      main.addEventListener('pointerdown', function (event) {
        if (!event.isTrusted) return;
        noteGalleryIntent(event.target.closest('[data-gallery-prev], [data-gallery-next]') ? 'arrow' : 'swipe');
      }, true);
      queryAll(page, '[data-gallery-thumbs]').forEach(function (thumbs) {
        thumbs.addEventListener('click', function (event) { if (event.isTrusted) noteGalleryIntent('thumbnail'); }, true);
      });

      // The first image counts once the gallery itself has been seen.
      observeVisibility([main], function () {
        var active = page.querySelector('.manual-gallery-image-active') || page.querySelector('.manual-gallery-image');
        if (active) trackGalleryImage(active, 'initial');
      });

      // Every later change of the active image, however it happened.
      var observer = new MutationObserver(function (records) {
        records.forEach(function (record) {
          var image = record.target;
          if (!image.classList.contains('manual-gallery-image-active')) return;
          if (String(record.oldValue || '').indexOf('manual-gallery-image-active') !== -1) return;
          var recent = Date.now() - galleryIntent.at < 1500 ? galleryIntent.method : '';
          trackGalleryImage(image, recent || (Date.now() - variantIntentAt < 1500 ? 'variant_change' : 'auto'));
        });
      });
      queryAll(page, '.manual-gallery-image').forEach(function (image) {
        observer.observe(image, { attributes: true, attributeFilter: ['class'], attributeOldValue: true });
      });
    });
  }

  var variantIntentAt = 0;

  // ---- FAQ, specs tabs, review categories ---------------------------------
  function bindEngagementClicks() {
    document.addEventListener('click', function (event) {
      if (!event.isTrusted) return; // ignore clicks the page or quiz scripts trigger themselves
      var target = event.target;
      if (!target || !target.closest) return;

      if (target.closest('.manual-option-btn, [data-option-name][data-option-value], [data-option-btn]')) variantIntentAt = Date.now();

      var faqTrigger = target.closest('[data-faq-trigger]');
      if (faqTrigger) {
        var item = faqTrigger.closest('[data-faq-item]');
        var opening = item && !item.classList.contains('is-open'); // read before the accordion toggles
        if (item && opening) {
          var faqId = item.getAttribute('data-faq-id') || slug(faqTrigger.querySelector('.manual-faq-text')?.textContent);
          if (once('faq:' + faqId)) {
            track('FAQ Opened', {
              faq_id: faqId,
              faq_position: parseInt(item.getAttribute('data-faq-position') || '0', 10) || null,
              question: faqTrigger.querySelector('.manual-faq-text')?.textContent?.trim(),
              product_handle: context.productHandle,
              page_name: context.pageName
            }, { properties: { faq_position: 'int' } });
          }
        }
      }

      var specsTab = target.closest('[data-specs-tab]');
      if (specsTab && !specsTab.classList.contains('manual-specs-tab-active')) {
        var tabId = specsTab.getAttribute('data-specs-tab');
        track(tabId === 'shipping' ? 'Shipping Tab Clicked' : 'Specs Tab Clicked', {
          tab_id: tabId,
          first_in_page_view: once('specs-tab:' + tabId),
          product_handle: context.productHandle,
          page_name: context.pageName
        });
      }

      var reviewFilter = target.closest('[data-filter]');
      if (reviewFilter && !reviewFilter.classList.contains('manual-filter-active')) {
        var group = reviewFilter.parentElement;
        var previous = group ? group.querySelector('[data-filter].manual-filter-active') : null;
        track('Review Category Selected', {
          category_id: reviewFilter.getAttribute('data-filter'),
          previous_category_id: previous ? previous.getAttribute('data-filter') : null,
          product_handle: context.productHandle,
          page_name: context.pageName,
          template_suffix: context.templateSuffix
        });
      }

      if (target.closest('[data-gallery-prev], [data-gallery-next]')) noteGalleryIntent('arrow');
    }, true);
  }

  function bindEngagement(root) {
    bindVideos(root);
    bindSections(root);
    bindGallery(root);
  }

  window.NordLabsCRO = {
    version: VERSION,
    context: context,
    track: track,
    setUserProperties: setUserProperties,
    setPageProperties: function (properties) { setProperties('page', properties); },
    tagElements: tagElements
  };

  patchFetch();
  setInitialPageProperties();
  captureEntryAndAttribution();
  bindInteractionTracking();
  bindEngagementClicks();
  tagElements(document);
  bindEngagement(document);
  detectQuizCompletion(document);

  var observer = new MutationObserver(function (records) {
    records.forEach(function (record) {
      Array.prototype.forEach.call(record.addedNodes || [], function (node) {
        if (node.nodeType !== 1) return;
        tagElements(node);
        bindEngagement(node);
        detectQuizCompletion(node);
      });
    });
    detectQuizCompletion(document);
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  var flushAttempts = 0;
  var flushTimer = window.setInterval(function () {
    flushAttempts += 1;
    flushPending();
    if (typeof window.FS === 'function' || flushAttempts >= 40) window.clearInterval(flushTimer);
  }, 250);
})();
